import { useState } from 'react';
import ChartHeader from '../Header';
import AreachartSkeleton from '../components/AreaChart/AreaChartSkeleton';
import BarChart, { BarChartProps } from '../components/BarChart';
import LineChart, { LineChartProps } from '../components/LineChart';
import PieChartSkeleton from '../components/PieChartSkeleton';
import SplitDonut, { SplitDonutProps } from '../components/PieChart';
import ConnectedDataChart, { ConnectedDataChartProps } from '../components/ConnectedDataChart';
import VerticalBarChartSkeleton from '../components/VerticalBarChartSkeleton';
import AreaChart, { AreaChartProps } from '../components/AreaChart';
import TinyLineChart, { TinyLineChartProps } from '../components/TinyLineChart';
import TinyLineSkeleton from '../components/TinyLineChart/TinyLineChartSkeleton';
import ProgressChart, { ProgressChartProps } from '../components/ProgressChart';
import ProgressChartSkeleton from '../components/ProgressChartSkeleton';
import ScoreClusterWaffleChartSkeleton from '../components/ScoreClusterWaffleChart/ScoreClusterWaffleChartSkeleton';
import ScoreClusterWaffleChart, { ScoreClusterWaffleChartProps } from '../components/ScoreClusterWaffleChart';
import InsightsScatterPlot, { InsightsScatterPlotChartProps } from '../components/InsightsScatterPlotChart';
import FraudDetectionVsApprovalReductionChart, { FraudDetectionVsApprovalReductionChartProps } from '../components/FraudDetectionVsApprovalReductionChart';
import EmptyState from '../../EmptyState';
import Column from '../../Column';
import FailureScreen from '../../FailureScreen';

export type ChartProps =
  | BarChartProps
  | SplitDonutProps
  | LineChartProps
  | ConnectedDataChartProps
  | AreaChartProps
  | TinyLineChartProps
  | ProgressChartProps
  | ScoreClusterWaffleChartProps
  | InsightsScatterPlotChartProps
  | FraudDetectionVsApprovalReductionChartProps;

export interface ChartContentData {
  chart: ChartProps;
  tabLabel: string;
  title: string;
  infoData?: string;
  isEmpty?: boolean;
}

export interface ChartContentProps {
  components: ChartContentData[];
  isLoading?: boolean;
  isError?: boolean;
  noWrapperStyle?: boolean;
}

const ChartContent: React.FC<ChartContentProps> = ({ components, isLoading, isError, noWrapperStyle = false }) => {
  const [selectedElement, setSelectedElement] = useState<number>(0);

  const tabs = components.map((t) => t.tabLabel);
  const chartTitle = components[selectedElement]?.title || '';
  const chartInfoData = components[selectedElement]?.infoData || '';

  const getComponent = (chartProps: ChartProps) => {
    switch (chartProps.type) {
      case 'barChart':
        return isLoading ? <AreachartSkeleton /> : <BarChart {...chartProps} />;
      case 'lineChart':
        return isLoading ? <AreachartSkeleton /> : <LineChart {...chartProps} />;
      case 'pieChart':
        return isLoading ? <PieChartSkeleton /> : <SplitDonut {...chartProps} />;
      case 'connectedData':
        return isLoading ? <VerticalBarChartSkeleton /> : <ConnectedDataChart {...chartProps} />;
      case 'areaChart':
        return isLoading ? <AreachartSkeleton /> : <AreaChart {...chartProps} />;
      case 'tinyLineChart':
        return isLoading ? <TinyLineSkeleton /> : <TinyLineChart {...chartProps} />;
      case 'progressChart':
        return isLoading ? <ProgressChartSkeleton /> : <ProgressChart {...chartProps} />;
      case 'scoreClusterWaffleChart':
        return isLoading ? <ScoreClusterWaffleChartSkeleton /> : <ScoreClusterWaffleChart {...chartProps} />;
      case 'insightScatterChart':
        return isLoading ? <AreachartSkeleton noYValues={true} /> : <InsightsScatterPlot {...chartProps} />;
      case 'fraudDetectionVsApprovalReductionChart':
        return isLoading ? <AreachartSkeleton /> : <FraudDetectionVsApprovalReductionChart {...chartProps} />;
    }
  };

  const noErrorContent = components[selectedElement]?.isEmpty ? (
    <EmptyState />
  ) : (
    components[selectedElement]?.chart.type && getComponent(components[selectedElement]?.chart as ChartProps)
  );

  return (
    <Column style={{ width: '100%', height: '100%' }} testId="chart-content">
      <>
        {!noWrapperStyle && (
          <ChartHeader title={chartTitle} tabs={tabs} selectedElement={selectedElement} setSelectedElement={setSelectedElement} infoData={chartInfoData} />
        )}
        {isError && <FailureScreen>Something went wrong. Please refresh the page and try again.</FailureScreen>}
        {!isError && noErrorContent}
      </>
    </Column>
  );
};

export default ChartContent;
